import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { toastSuccess, toastInfo, toastError, errorMessage } from '../../../lib/toast';
import EmailOptionsTab from './EmailOptionsTab';
import ProfileCard from '../ui/ProfileCard';
import CardState from '../ui/CardState';

// Mirrors MARITAL_SELECTION_KEYS in server/services/odoo/personalFields.js.
// The server is the authority and re-validates on write; this list only decides
// what the dropdown offers.
const MARITAL_OPTIONS = [
  ['', '—'],
  ['single', 'Single'],
  ['married', 'Married'],
  ['cohabitant', 'Legal cohabitant'],
  ['widower', 'Widower'],
  ['divorced', 'Divorced'],
];

// Mirrors CERTIFICATE_SELECTION_KEYS on the server.
const CERTIFICATE_OPTIONS = [
  ['', '—'],
  ['graduate', 'Graduate'],
  ['bachelor', 'Bachelor'],
  ['master', 'Master'],
  ['doctor', 'Doctor'],
  ['other', 'Other'],
];

// Server {error:code} -> human copy.
const ERRORS = {
  no_hr_record: 'No HR record linked to your account yet.',
  odoo_unavailable: "Couldn't reach the HR system. Try again shortly.",
  odoo_access_denied: "Couldn't reach the HR system. Try again shortly.",
  rate_limited: 'Too many changes just now. Wait a minute and try again.',
  // The server rejects the payload as a whole and names no field, so the copy
  // must not promise highlighting: it lists the fields that carry a format.
  invalid_input: "Some details weren't accepted. Check dates, phone numbers and the email address, then try again.",
};

const UNAVAILABLE = {
  no_hr_record: 'No HR record linked yet. Your details appear once your Odoo record syncs.',
  odoo_unavailable: "Couldn't reach the HR system. Your details will appear once it's back.",
};

/**
 * Flatten the server's shape into the flat form state the inputs bind to.
 *
 * Read-only fields (ID number, SSN, visa, work permit) are deliberately NOT in
 * here: they are displayed straight from `fields` and must never become part of
 * the editable form state, or a dirty-check could try to submit them.
 */
const RELATION_KEYS = ['private_country_id', 'private_state_id', 'country_of_birth'];

const EDITABLE_KEYS = [
  'marital', 'spouse_complete_name', 'spouse_birthdate',
  'birthday', 'place_of_birth', 'country_of_birth',
  'work_phone', 'private_phone', 'private_email', 'children',
  'private_street', 'private_street2', 'private_city', 'private_zip',
  'private_state_id', 'private_country_id',
  'emergency_contact', 'emergency_phone',
  'passport_id', 'certificate', 'study_field', 'study_school',
];

function toForm(fields) {
  if (!fields) return {};
  const out = {};
  for (const key of EDITABLE_KEYS) {
    const v = fields[key];
    if (RELATION_KEYS.includes(key)) out[key] = v?.id || '';
    else if (v === null || v === undefined || v === false) out[key] = '';
    else out[key] = v;
  }
  return out;
}

function Field({ label, htmlFor, children, hint, error, wide }) {
  return (
    <div className={`pers-field${wide ? ' pers-field--wide' : ''}`}>
      <label className="pers-field__label" htmlFor={htmlFor}>{label}</label>
      {children}
      {error
        ? <span className="pers-field__error" role="status">{error}</span>
        : hint && <span className="pers-field__hint">{hint}</span>}
    </div>
  );
}

/**
 * A value the employee may read but not change — ID number, SSN, visa and work
 * permit. Rendered as text on a muted surface rather than a disabled input:
 * a greyed-out input invites clicking and reads as broken, whereas plain text
 * reads as "this is a fact, not a control".
 *
 * Uses a <span> with an id rather than a <label for>, since there is no form
 * control to label.
 */
function ReadOnlyField({ label, value, id }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="pers-field">
      <span className="pers-field__label" id={`${id}-label`}>{label}</span>
      <span
        className={`pers-readonly${empty ? ' pers-readonly--empty' : ''}`}
        aria-labelledby={`${id}-label`}
      >
        {empty ? 'Not on record' : value}
      </span>
    </div>
  );
}

function PersonalDetails({ onSaved, onDirtyChange }) {
  const [state, setState] = useState('loading'); // loading | ok | unavailable | error
  const [reason, setReason] = useState(null);
  const [form, setForm] = useState({});
  const [saved, setSaved] = useState({});
  const [descriptors, setDescriptors] = useState([]);
  const [raw, setRaw] = useState({});
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  // A failed list is not an empty one: with no options every select would read
  // "—" and look unset while the saved id is still in the form.
  const [countriesFailed, setCountriesFailed] = useState(false);
  const [statesFailed, setStatesFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await axios.get(`${API_BASE_URL}/api/profile/personal`, { withCredentials: true });
      if (!r.data.available) {
        setReason(r.data.reason);
        setState('unavailable');
        return;
      }
      const f = toForm(r.data.fields);
      setForm(f);
      setSaved(f);
      // Read-only values are rendered straight from the server payload; they
      // never enter `form`, so a dirty-check can never try to submit them.
      setRaw(r.data.fields || {});
      setDescriptors(r.data.field_descriptors || []);
      setState('ok');
    } catch {
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Countries are only needed once the form is actually usable.
  useEffect(() => {
    if (state !== 'ok') return undefined;
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/api/profile/personal/countries`, { withCredentials: true });
        if (active) { setCountries(r.data.countries || []); setCountriesFailed(false); }
      } catch {
        if (active) { setCountries([]); setCountriesFailed(true); }
      }
    })();
    return () => { active = false; };
  }, [state]);

  // States depend on the chosen country; an unset country means no state list.
  useEffect(() => {
    const countryId = form.private_country_id;
    if (!countryId) { setStates([]); setStatesFailed(false); return undefined; }
    let active = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API_BASE_URL}/api/profile/personal/states?country_id=${countryId}`,
          { withCredentials: true }
        );
        if (active) { setStates(r.data.states || []); setStatesFailed(false); }
      } catch {
        if (active) { setStates([]); setStatesFailed(true); }
      }
    })();
    return () => { active = false; };
  }, [form.private_country_id]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      // Changing country invalidates the state: a governorate from the old
      // country would be silently wrong rather than merely stale.
      if (key === 'private_country_id') return { ...f, private_country_id: value, private_state_id: '' };
      return { ...f, [key]: value };
    });
  };

  const dirty = state === 'ok' && JSON.stringify(form) !== JSON.stringify(saved);

  // The page asks before a tab switch would throw these edits away.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, [onDirtyChange]);
  // A reload or tab close would drop the edits too: let the browser ask. Only
  // while dirty, so a clean or just-saved form never prompts.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        // Empty select -> null, so the server clears the many2one rather than
        // trying to write the empty string as an id.
        private_country_id: form.private_country_id ? Number(form.private_country_id) : null,
        private_state_id: form.private_state_id ? Number(form.private_state_id) : null,
      };
      const r = await axios.put(`${API_BASE_URL}/api/profile/personal`, body, { withCredentials: true });
      const next = r.data.fields ? toForm(r.data.fields) : form;
      setForm(next);
      setSaved(next);
      // The server diffs against Odoo; an edit that only added spaces or was
      // typed back to its old value changes nothing and is not written.
      if (Array.isArray(r.data.changed) && r.data.changed.length === 0) toastInfo('No changes to save. Your details are already up to date.');
      else toastSuccess('Personal details saved.');
      if (onSaved) onSaved();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't save your details. Try again.", ERRORS));
    } finally {
      setSaving(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="profile-card-grid">
        <ProfileCard title="Personal details">
          <CardState kind="loading" label="Loading your details" lines={4} />
        </ProfileCard>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="profile-card-grid">
        <ProfileCard title="Personal details">
          <CardState kind="error" thing="your details" retry />
          <button type="button" className="btn btn-secondary btn-small pers-retry" onClick={load}>Try again</button>
        </ProfileCard>
      </div>
    );
  }

  if (state === 'unavailable') {
    return (
      <div className="profile-card-grid">
        <ProfileCard title="Personal details">
          <CardState kind="empty">{UNAVAILABLE[reason] || UNAVAILABLE.no_hr_record}</CardState>
        </ProfileCard>
      </div>
    );
  }

  // The server tells us which fields are HR-owned; the client never hardcodes
  // the list, so adding one server-side needs no change here.
  const readOnlyDescriptors = descriptors.filter((d) => d.readOnly);

  const showSpouse = form.marital === 'married' || form.marital === 'cohabitant';

  return (
    <form className="profile-card-grid profile-card-grid--personal" onSubmit={submit}>
      {/* The intro belongs to the whole form, not to the first card: every
          section below writes to the same Odoo record. */}
      <p className="pers-lede">
        These details live on your Odoo HR record. Changes you make here are written
        straight to it.
      </p>

      {/* Card order follows how someone thinks about themselves: who I am, my
          family, where I live, who to call, what I studied, and finally the
          documents HR holds on me.

          None of the editable cards carries a "Saved to Odoo" tag: the lede
          above already says it once for the whole form, and stamping the same
          badge on five cards is noise rather than information. The only tag on
          this tab is "Managed by HR" on Identity documents, which marks the one
          card that behaves DIFFERENTLY -- a tag every card shares tells the
          reader nothing. */}
      <section className="profile-info-card">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">About you</h2>
        </header>
        <div className="pers-grid">
          <Field label="Date of birth" htmlFor="pers-dob">
            <input id="pers-dob" type="date" value={form.birthday} onChange={set('birthday')} />
          </Field>
          <Field label="Place of birth" htmlFor="pers-pob">
            <input id="pers-pob" type="text" dir="auto" maxLength={200}
              value={form.place_of_birth} onChange={set('place_of_birth')} />
          </Field>
          <Field label="Country of birth" htmlFor="pers-cob" wide error={countriesFailed ? "Couldn't load the country list. Refresh to try again." : undefined}>
            <select id="pers-cob" value={form.country_of_birth} onChange={set('country_of_birth')}>
              <option value="">—</option>
              {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Work phone" htmlFor="pers-wphone">
            <input id="pers-wphone" type="tel" maxLength={200}
              value={form.work_phone} onChange={set('work_phone')} />
          </Field>
          <Field label="Private phone" htmlFor="pers-pphone">
            <input id="pers-pphone" type="tel" maxLength={200}
              value={form.private_phone} onChange={set('private_phone')} />
          </Field>
          <Field label="Private email" htmlFor="pers-pemail">
            <input id="pers-pemail" type="email" maxLength={200}
              value={form.private_email} onChange={set('private_email')} />
          </Field>
        </div>
      </section>

      <section className="profile-info-card">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Family</h2>
        </header>
        <div className="pers-grid">
          <Field label="Marital status" htmlFor="pers-marital">
            <select id="pers-marital" value={form.marital} onChange={set('marital')}>
              {MARITAL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Dependent children" htmlFor="pers-children">
            <input id="pers-children" type="number" min="0" max="50"
              value={form.children} onChange={set('children')} />
          </Field>

          {/* Spouse fields only make sense for a marital status that has one.
              They are not cleared when hidden -- HR may have recorded them and
              a status change should not silently delete data. */}
          {showSpouse && (
            <>
              <Field label="Spouse's name" htmlFor="pers-spouse">
                <input
                  id="pers-spouse" type="text" dir="auto" maxLength={200}
                  value={form.spouse_complete_name} onChange={set('spouse_complete_name')}
                />
              </Field>
              <Field label="Spouse's birthdate" htmlFor="pers-spouse-dob">
                <input
                  id="pers-spouse-dob" type="date"
                  value={form.spouse_birthdate} onChange={set('spouse_birthdate')}
                />
              </Field>
            </>
          )}
        </div>
      </section>

      <section className="profile-info-card">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Home address</h2>
        </header>
        <div className="pers-grid">
          <Field label="Street" htmlFor="pers-street" wide>
            <input id="pers-street" type="text" dir="auto" maxLength={500}
              value={form.private_street} onChange={set('private_street')} />
          </Field>
          <Field label="Street line 2" htmlFor="pers-street2" wide>
            <input id="pers-street2" type="text" dir="auto" maxLength={500}
              value={form.private_street2} onChange={set('private_street2')} />
          </Field>
          <Field label="City" htmlFor="pers-city">
            <input id="pers-city" type="text" dir="auto" maxLength={200}
              value={form.private_city} onChange={set('private_city')} />
          </Field>
          <Field label="ZIP / postal code" htmlFor="pers-zip">
            <input id="pers-zip" type="text" dir="auto" maxLength={200}
              value={form.private_zip} onChange={set('private_zip')} />
          </Field>
          <Field label="Country" htmlFor="pers-country" error={countriesFailed ? "Couldn't load the country list. Refresh to try again." : undefined}>
            <select id="pers-country" value={form.private_country_id} onChange={set('private_country_id')}>
              <option value="">—</option>
              {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field
            label="State / governorate"
            htmlFor="pers-state"
            hint={!form.private_country_id ? 'Pick a country first.' : undefined}
            error={statesFailed ? "Couldn't load the states. Refresh to try again." : undefined}
          >
            <select
              id="pers-state" value={form.private_state_id} onChange={set('private_state_id')}
              disabled={!form.private_country_id || states.length === 0}
            >
              <option value="">—</option>
              {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        </div>
      </section>

      <section className="profile-info-card">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Emergency contact</h2>
        </header>
        <p className="pers-hint">Who we should call if something happens at work.</p>
        <div className="pers-grid">
          <Field label="Contact name" htmlFor="pers-emg-name">
            <input id="pers-emg-name" type="text" dir="auto" maxLength={200}
              value={form.emergency_contact} onChange={set('emergency_contact')} />
          </Field>
          <Field label="Contact phone" htmlFor="pers-emg-phone">
            <input id="pers-emg-phone" type="tel" maxLength={200}
              value={form.emergency_phone} onChange={set('emergency_phone')} />
          </Field>
        </div>
      </section>

      <section className="profile-info-card">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Education</h2>
        </header>
        <div className="pers-grid">
          <Field label="Certificate level" htmlFor="pers-cert">
            <select id="pers-cert" value={form.certificate} onChange={set('certificate')}>
              {CERTIFICATE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Field of study" htmlFor="pers-study-field">
            <input id="pers-study-field" type="text" dir="auto" maxLength={200}
              value={form.study_field} onChange={set('study_field')} />
          </Field>
          <Field label="School / university" htmlFor="pers-school" wide>
            <input id="pers-school" type="text" dir="auto" maxLength={200}
              value={form.study_school} onChange={set('study_school')} />
          </Field>
        </div>
      </section>

      <section className="profile-info-card">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Identity documents</h2>
          <span className="profile-source-tag">Managed by HR</span>
        </header>
        <p className="pers-hint">
          Your passport number is yours to keep current. Anything else here is recorded
          by HR — contact them if it needs correcting.
        </p>
        <div className="pers-grid">
          <Field label="Passport number" htmlFor="pers-passport">
            <input id="pers-passport" type="text" dir="auto" maxLength={200}
              value={form.passport_id} onChange={set('passport_id')} />
          </Field>
          {/* Read-only identifiers, driven by the server's field_descriptors so
              the client never hardcodes which fields are HR-owned. */}
          {readOnlyDescriptors.map((d) => (
            <ReadOnlyField key={d.key} id={`pers-ro-${d.key}`} label={d.label} value={raw[d.key]} />
          ))}
        </div>
      </section>

      <div className="pers-actions">
        <button type="submit" className="btn btn-primary" disabled={!dirty || saving} aria-busy={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {dirty && !saving && <span className="pers-actions__note">You have unsaved changes.</span>}
      </div>
    </form>
  );
}

/**
 * The Personal tab: your Odoo HR details, plus email options.
 *
 * Email options moved here when the profile went from six tabs to four. It
 * renders OUTSIDE the Odoo-dependent details above, deliberately: the details
 * are read live from Odoo and collapse to an error or unavailable card when
 * Odoo is down, whereas email preferences are Nexus's own data and have no
 * reason to disappear with it.
 */
export default function PersonalTab({ data, onSaved, onDirtyChange }) {
  return (
    <>
      <PersonalDetails onSaved={onSaved} onDirtyChange={onDirtyChange} />
      <div className="profile-card-grid profile-card-grid--personal">
        <EmailOptionsTab data={data} />
      </div>
    </>
  );
}
